/**
 * GALLEON REALISM (2026-11, owner: "more realistic … ships").
 *
 * The pirate fleet is built from axis-aligned boxes in `maps.ts` (pirateShip),
 * and boxes are exactly right for COLLISION — but square masts, sails that are
 * 20 cm stone slabs, iron cannon that are bricks and a ship with no rigging at
 * all are what made it read as a toy. This module redraws the parts that the
 * eye reads as "ship" and leaves every collider untouched:
 *
 *   - `shipSpar`  masts + yards → turned, tapered spars (inside the box)
 *   - `shipSail`  canvas → a billowed cloth surface with seams and bolt rope
 *   - `shipGunE/W` barrels → cast-iron cannon with reinforce rings + muzzle
 *   - `shipHull`  hull sides → a row of open gunports on the gun deck
 *   - `shipStern` transom → stern-gallery windows in gilt frames
 *   - (derived)   standing rigging: shrouds + ratlines + stays + bowsprit
 *
 * The tags are set in maps.ts and are VISUAL ONLY (movement.ts reads only
 * "water" / "practice_mover"), so client and server collide against exactly the
 * boxes they always did. The renderer skips drawing the boxes this module
 * REPLACES (`shipReplaces`) and adds the group from `buildShipDressing`.
 *
 * Budget: everything is merged into ≤ 6 meshes for the whole fleet (spars,
 * canvas, rope, iron, port/window paint, glass), built once at map load. No
 * per-frame work, no per-frame allocation.
 *
 * Fit rules (same spirit as mapDressing's ≤ 0.26 m "proud" contract):
 *   - turned spars and barrels stay within ~0.12 m of their collider box;
 *   - gunports / stern lights are ≤ 0.24 m proud of their wall;
 *   - canvas billows FORWARD at most SAIL_BILLOW past the box's back face. Cloth
 *     is the one deliberate exception to the 0.26 m rule: a flat sail looks
 *     like a board, and nobody fights on a sail;
 *   - rigging is rope: walk-through, and it lands on the bulwark top so it is
 *     never across the walkable deck at head height.
 * `agent/bench-ship.mjs` asserts these from the shipped map.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { MapBox } from "@neon-purge/shared";
import { surfaceMaps } from "./mapTextures.js";

/** Tags whose BOX is not drawn — this module draws a replacement. */
const REPLACED = new Set(["shipSpar", "shipSail", "shipGunE", "shipGunW"]);
/** Tags this module reads (replaced + dressed-in-place). */
const SHIP_TAGS = new Set([...REPLACED, "shipHull", "shipStern", "shipTop"]);

export function shipReplaces(box: MapBox): boolean {
  return box.tag !== undefined && REPLACED.has(box.tag);
}
export function isShipBox(box: MapBox): boolean {
  return box.tag !== undefined && SHIP_TAGS.has(box.tag);
}

// ---------------------------------------------------------------------------
// PURE LAYOUT (no three.js) — what the bench checks
// ---------------------------------------------------------------------------

/** Half-beam of the galleon at the bulwark (hull sides are at local x ±8,
 *  0.7 thick; the rail on top of them is 0.3 wide). */
export const SHIP_HALF_BEAM = 8;
/** Bulwark rail top (rail box centre 5.9, height 1.4). Chainplates land here. */
export const SHIP_RAIL_TOP = 6.6;
/** Forward billow of the canvas, measured from the sail box's BACK face. */
export const SAIL_BILLOW = 0.75;
/** A ship is every tagged box within this XZ radius of a mast. */
const SHIP_RADIUS = 45;

export interface RopeSeg { a: [number, number, number]; b: [number, number, number]; r: number }
export interface Port { x: number; y: number; z: number; nx: number }

export interface Mast {
  x: number; z: number; y0: number; y1: number; thick: number; hounds: number; topYard: number; topYardW: number;
  /** The fighting TOP (crow's nest platform) on this mast: half-width + surface y. */
  top: { half: number; y: number; under: number } | null;
}

/** Masts = VERTICAL spars (taller than wide). Yards are the horizontal ones. */
export function shipMasts(boxes: readonly MapBox[]): Mast[] {
  const spars = boxes.filter((b) => b.tag === "shipSpar");
  const masts: Mast[] = [];
  for (const m of spars) {
    if (m.s[1] <= Math.max(m.s[0], m.s[2])) continue;
    const y0 = m.p[1] - m.s[1] / 2, y1 = m.p[1] + m.s[1] / 2;
    // the yards that cross this mast, lowest first
    const yards = spars
      .filter((y) => y !== m && y.s[1] < y.s[0] && Math.abs(y.p[0] - m.p[0]) < 0.5 && Math.abs(y.p[2] - m.p[2]) < 0.5)
      .sort((a, b) => a.p[1] - b.p[1]);
    const low = yards[0], top = yards[yards.length - 1];
    const plat = boxes.find((t) => t.tag === "shipTop" && Math.abs(t.p[0] - m.p[0]) < 0.5 && Math.abs(t.p[2] - m.p[2]) < 0.5);
    masts.push({
      top: plat ? { half: plat.s[0] / 2, y: plat.p[1] + plat.s[1] / 2, under: plat.p[1] - plat.s[1] / 2 } : null,
      x: m.p[0], z: m.p[2], y0, y1, thick: m.s[0],
      hounds: low ? low.p[1] + 0.35 : y0 + (y1 - y0) * 0.45,
      topYard: top ? top.p[1] : y0 + (y1 - y0) * 0.74,
      topYardW: top ? top.s[0] : 6,
    });
  }
  return masts;
}

/** Group masts into ships (masts of one hull stand on a fore-aft line). */
function shipsOf(masts: Mast[]): Mast[][] {
  const ships: Mast[][] = [];
  for (const m of masts) {
    const s = ships.find((g) => Math.abs(g[0].x - m.x) < 2 && Math.abs(g[0].z - m.z) < SHIP_RADIUS);
    if (s) s.push(m); else ships.push([m]);
  }
  for (const s of ships) s.sort((a, b) => a.z - b.z); // stern → bow
  return ships;
}

/** Bowsprit for a ship whose foremost mast is `fore`: a spar from the prow up
 *  and out over the figurehead. Local numbers from maps.ts (prow block ends at
 *  z+38 from the hull origin, i.e. fore.z + 16 for the z22 foremast). */
export function bowsprit(fore: Mast): { a: [number, number, number]; b: [number, number, number] } {
  return { a: [fore.x, 5.6, fore.z + 13], b: [fore.x, 9.6, fore.z + 25] };
}

/** Where a shroud is made fast on one side of a mast at fore-aft position z.
 *  On the open main deck that is a CHANNEL — the chain-wale ledge outboard of
 *  the hull at about rail height, which is how real shrouds clear the bulwark
 *  (and here: the deck cannon and the boarding gap). On a raised deck
 *  (forecastle / quarterdeck) it is that deck's own rail top, since a channel
 *  down at main-deck height would drag the rope through the raised deck slab.
 *  null = no hull at z on that side (the boarding gap): skip this foot. */
function shroudFoot(solids: readonly MapBox[], hulls: readonly MapBox[], m: Mast, side: number, z: number): { p: [number, number, number]; channel: boolean } | null {
  let best: MapBox | null = null, bestTop = -Infinity;
  for (const b of solids) {
    if (Math.abs(z - b.p[2]) > b.s[2] / 2) continue;
    for (const dx of [6.8, 7.0, 7.2, 7.4]) {
      const x = m.x + side * dx;
      if (Math.abs(x - b.p[0]) > b.s[0] / 2) continue;
      const top = b.p[1] + b.s[1] / 2;
      if (top < m.hounds - 2 && top > bestTop) { bestTop = top; best = b; }
    }
  }
  if (best && bestTop > SHIP_RAIL_TOP + 0.2) {
    const edge = best.p[0] + side * best.s[0] / 2;
    return { p: [edge + side * 0.12, bestTop, z], channel: false };
  }
  const hull = hulls.some((h) => Math.sign(h.p[0] - m.x) === side && Math.abs(z - h.p[2]) <= h.s[2] / 2 - 0.3);
  if (!hull) return null;
  return { p: [m.x + side * CHANNEL_X, CHANNEL_Y, z], channel: true };
}
/** Channel ledge: outboard edge x (from the ship centreline) and top y. The
 *  hull's outer face is at 8.35, so the ledge is 0.25 proud — inside the
 *  mapDressing 0.26 m contract; at y 6.2 it clears the deck cannon barrels
 *  (top 5.75) and the hull top (5.2), and the rail's outer face (8.15) is
 *  cleared by the rope as it rises inboard. */
export const CHANNEL_X = 8.5, CHANNEL_Y = 6.2, CHANNEL_OUT = 8.6;

export interface Channel { x0: number; x1: number; y: number; z0: number; z1: number }

function rigLayout(boxes: readonly MapBox[]): { ropes: RopeSeg[]; channels: Channel[] } {
  const ropes: RopeSeg[] = [];
  const channels: Channel[] = [];
  const SHROUD_R = 0.045, RAT_R = 0.016, STAY_R = 0.06;
  const solids = boxes.filter((b) => b.tag !== "water" && !shipReplaces(b));
  const hulls = boxes.filter((b) => b.tag === "shipHull");
  const ships = shipsOf(shipMasts(boxes));
  for (const ship of ships) {
    for (const m of ship) {
      const off = m.thick / 2 + 0.05;
      for (const side of [-1, 1]) {
        // LOWER SHROUDS: hounds (just above the course yard) → the channel,
        // leading AFT of the mast so they never cross the canvas, which hangs
        // FORWARD of it (sail boxes sit at mast z + 0.3). Four feet a metre
        // apart, stepping further aft past any stretch with no hull under it.
        const feet: { p: [number, number, number]; channel: boolean }[] = [];
        for (let dz = -0.9; feet.length < 4 && dz > -9; dz -= 1.0) {
          const f = shroudFoot(solids, hulls, m, side, m.z + dz);
          if (f && (feet.length === 0 || f.channel === feet[0].channel)) feet.push(f);
        }
        if (feet.length < 2) continue;
        const foot = feet.map((f) => f.p);
        const top: [number, number, number][] = foot.map((_, i) => [m.x + side * off, m.hounds - i * 0.25, m.z - 0.35 - i * 0.12]);
        for (let i = 0; i < foot.length; i++) ropes.push({ a: top[i], b: foot[i], r: SHROUD_R });
        if (feet[0].channel) {
          const zs = foot.map((f) => f[2]);
          channels.push({ x0: m.x + side * 8.35, x1: m.x + side * CHANNEL_OUT, y: CHANNEL_Y, z0: Math.min(...zs) - 0.3, z1: Math.max(...zs) + 0.3 }); // 0.3 = the foot's own hull inset, so a ledge never overhangs the boarding gap
        }
        // RATLINES: horizontal rungs tied across the shrouds every 0.5 m
        const yLo = Math.max(...foot.map((f) => f[1])) + 0.6, yHi = m.hounds - 1.2;
        for (let y = yLo; y <= yHi; y += 0.5) {
          const pts = top.map((t, i) => {
            const f = (y - foot[i][1]) / (t[1] - foot[i][1]);
            return [foot[i][0] + (t[0] - foot[i][0]) * f, y, foot[i][2] + (t[2] - foot[i][2]) * f] as [number, number, number];
          });
          for (let i = 0; i + 1 < pts.length; i++) ropes.push({ a: pts[i], b: pts[i + 1], r: RAT_R });
        }
        // TOPMAST SHROUDS: masthead → down and out, aft of the mast. With a
        // fighting top they land on deadeyes just OUTSIDE its rim and FUTTOCK
        // shrouds carry the load back in to the mast below — the real
        // arrangement, and the only one that doesn't run rope through the
        // platform and its rail.
        const yt = m.y1 - 0.6;
        if (m.top) {
          const rim = m.top.half + 0.6; // clears the top's 1 m rail on the way down
          ropes.push({ a: [m.x + side * off * 0.8, yt, m.z - 0.3], b: [m.x + side * rim, m.top.y - 0.05, m.z - 0.3], r: SHROUD_R * 0.8 });
          ropes.push({ a: [m.x + side * rim, m.top.y - 0.05, m.z - 0.3], b: [m.x + side * off, m.top.under - 2.4, m.z - 0.3], r: SHROUD_R * 0.8 });
        } else {
          ropes.push({ a: [m.x + side * off * 0.8, yt, m.z - 0.3], b: [m.x + side * 2.6, m.hounds, m.z - 0.5], r: SHROUD_R * 0.8 });
        }
      }
    }
    // STAYS (fore-and-aft, centreline): each masthead → the next mast forward
    // at its hounds; the foremast → the bowsprit end. All of them run high
    // above the decks (the lowest end is a hounds, ~10 m up).
    const tallest = Math.max(...ship.map((s) => s.y1 - s.y0));
    for (let i = 0; i < ship.length; i++) {
      const m = ship[i];
      const next = ship[i + 1];
      const a: [number, number, number] = [m.x, m.y1 - 0.5, m.z + m.thick / 2];
      const b: [number, number, number] = next ? [next.x, next.hounds + 0.2, next.z - next.thick / 2] : bowsprit(m).b;
      ropes.push({ a, b, r: STAY_R });
      // BACKSTAYS from the mainmast head down to a channel well aft
      if (m.y1 - m.y0 === tallest) {
        for (const side of [-1, 1]) {
          const f = shroudFoot(solids, hulls, m, side, m.z - 9);
          if (f) ropes.push({ a: [m.x + side * 0.3, m.y1 - 0.8, m.z - 0.4], b: f.p, r: SHROUD_R });
        }
      }
    }
  }
  return { ropes, channels };
}

/** Every rope of the standing rigging, as straight segments. */
export function shipRigging(boxes: readonly MapBox[]): RopeSeg[] {
  return rigLayout(boxes).ropes;
}
/** The chain-wale ledges the main-deck shrouds land on. */
export function shipChannels(boxes: readonly MapBox[]): Channel[] {
  return rigLayout(boxes).channels;
}

/** Open gunports along each hull side, on the gun deck (hold floor 1.8 → deck
 *  4.4), centred at y 3.1. Every 4 m, kept 1.4 m clear of the box ends (the
 *  boarding gap and the bow/stern blocks). */
export function shipGunports(boxes: readonly MapBox[]): Port[] {
  const out: Port[] = [];
  for (const b of boxes) {
    if (b.tag !== "shipHull") continue;
    // outward = away from the ship's centreline: the matching hull side is the
    // box on the same z span at the mirrored x. Without it, fall back to the sign.
    const twin = boxes.find((o) => o !== b && o.tag === "shipHull" && Math.abs(o.p[2] - b.p[2]) < 0.01 && Math.abs(o.p[1] - b.p[1]) < 0.01);
    const nx = twin ? Math.sign(b.p[0] - twin.p[0]) : Math.sign(b.p[0]) || 1;
    const face = b.p[0] + nx * b.s[0] / 2;
    const z0 = b.p[2] - b.s[2] / 2 + 1.4, z1 = b.p[2] + b.s[2] / 2 - 1.4;
    const n = Math.max(0, Math.floor((z1 - z0) / 4) + 1);
    const used = (n - 1) * 4;
    for (let i = 0; i < n; i++) out.push({ x: face, y: 3.1, z: (z0 + z1) / 2 - used / 2 + i * 4, nx });
  }
  return out;
}

// ---------------------------------------------------------------------------
// GEOMETRY
// ---------------------------------------------------------------------------

const _c = new THREE.Color();
function paint(g: THREE.BufferGeometry, color: string | THREE.Color, shadeTop = 0): THREE.BufferGeometry {
  const pos = g.getAttribute("position");
  const col = new Float32Array(pos.count * 3);
  _c.set(color as THREE.ColorRepresentation);
  g.computeBoundingBox();
  const bb = g.boundingBox!;
  const hh = Math.max(1e-6, bb.max.y - bb.min.y);
  for (let i = 0; i < pos.count; i++) {
    const f = shadeTop ? 1 - shadeTop + shadeTop * ((pos.getY(i) - bb.min.y) / hh) : 1;
    col[i * 3] = _c.r * f; col[i * 3 + 1] = _c.g * f; col[i * 3 + 2] = _c.b * f;
  }
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  return g;
}

/** A solid of revolution around +Y from (radius, y) points, uv v = y / tile. */
function lathe(pts: [number, number][], seg: number, uTile: number, vTile: number): THREE.BufferGeometry {
  const g = new THREE.LatheGeometry(pts.map(([r, y]) => new THREE.Vector2(r, y)), seg);
  const pos = g.getAttribute("position"), uv = g.getAttribute("uv");
  const rMax = Math.max(...pts.map((p) => p[0]));
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * (2 * Math.PI * rMax) / uTile, pos.getY(i) / vTile);
  return g.toNonIndexed();
}

/** Thin cylinder from a to b (rope). */
const _up = new THREE.Vector3(0, 1, 0), _va = new THREE.Vector3(), _vb = new THREE.Vector3(), _q = new THREE.Quaternion();
function rope(s: RopeSeg, seg: number): THREE.BufferGeometry {
  _va.set(...s.a); _vb.set(...s.b);
  const len = _va.distanceTo(_vb);
  const g = new THREE.CylinderGeometry(s.r, s.r, len, seg, 1, true);
  g.deleteAttribute("uv");
  _q.setFromUnitVectors(_up, _vb.clone().sub(_va).normalize());
  g.applyQuaternion(_q);
  g.translate((s.a[0] + s.b[0]) / 2, (s.a[1] + s.b[1]) / 2, (s.a[2] + s.b[2]) / 2);
  return g.toNonIndexed();
}

/** Turned spar for a `shipSpar` box: vertical → mast tapering upward with
 *  iron hoops; horizontal (along x) → yard, thick in the slings, thin at the
 *  arms. Max radius ≈ 0.56 × box thickness: the corners of the square collider
 *  sit ≤ ~0.12 m outside the round, the faces ≤ ~0.07 m inside it. */
export function sparGeo(b: MapBox): THREE.BufferGeometry[] {
  const [w, h, d] = b.s;
  const vertical = h > Math.max(w, d);
  const t = vertical ? Math.min(w, d) : Math.min(h, d);
  const L = vertical ? h : w;
  const rMax = t * 0.56;
  const pts: [number, number][] = vertical
    ? [[0, 0], [rMax, 0], [rMax, L * 0.3], [rMax * 0.9, L * 0.6], [rMax * 0.72, L * 0.92], [rMax * 0.6, L], [0, L]]
    : [[0, 0], [rMax * 0.5, 0], [rMax * 0.62, L * 0.1], [rMax * 0.9, L * 0.32], [rMax, L * 0.5], [rMax * 0.9, L * 0.68], [rMax * 0.62, L * 0.9], [rMax * 0.5, L], [0, L]];
  const g = lathe(pts, vertical ? 14 : 10, 1.2, 2.4);
  g.translate(0, -L / 2, 0);
  if (!vertical) g.rotateZ(Math.PI / 2);
  g.translate(b.p[0], b.p[1], b.p[2]);
  paint(g, b.color ?? "#4a3320");
  const out = [g];
  if (vertical) {
    // IRON HOOPS binding the made mast, the lower 60 %
    for (let y = 0.8; y < L * 0.6; y += 1.6) {
      const r = rMax * (y < L * 0.3 ? 1 : 1 - 0.1 * (y - L * 0.3) / (L * 0.3)) + 0.02;
      const hoop = new THREE.CylinderGeometry(r, r, 0.12, 14, 1, true).toNonIndexed();
      hoop.deleteAttribute("uv");
      hoop.translate(b.p[0], b.p[1] - L / 2 + y, b.p[2]);
      out.push(paint(hoop, "#1d1b1a"));
    }
  }
  return out;
}

/** Billowed canvas for a `shipSail` box (in the X-Y plane, billowing +z). */
export function sailGeo(b: MapBox): THREE.BufferGeometry {
  const [w, h, d] = b.s;
  const SX = 18, SY = 14;
  const g = new THREE.PlaneGeometry(w, h, SX, SY);
  const pos = g.getAttribute("position");
  const back = -d / 2;
  for (let i = 0; i < pos.count; i++) {
    const u = pos.getX(i) / w + 0.5;           // 0 port → 1 starboard
    const t = 0.5 - pos.getY(i) / h;           // 0 at the yard → 1 at the foot
    // the head is laced to the yard (no billow there); the belly fills lowest
    // two-thirds down; the leeches hold a little shape of their own
    const across = 0.32 + 0.68 * Math.sin(Math.PI * u);
    const down = Math.sin(Math.PI * 0.72 * t) / Math.sin(Math.PI * 0.72 * 0.69);
    pos.setZ(i, back + SAIL_BILLOW * across * Math.min(1, down));
    // ROACH: the foot is cut in a shallow curve and lifts as the cloth fills
    pos.setY(i, pos.getY(i) + 0.22 * Math.sin(Math.PI * u) * t * t * t);
  }
  g.computeVertexNormals();
  g.translate(b.p[0], b.p[1], b.p[2]);
  return paint(g.toNonIndexed(), b.color ?? "#e8e0cf");
}

/** Cast-iron cannon for a `shipGunE|W` barrel box (axis x, muzzle outboard). */
export function gunGeo(b: MapBox): THREE.BufferGeometry {
  const L = b.s[0], R = Math.min(b.s[1], b.s[2]) / 2;
  // breech knob (cascabel) at y=0 → muzzle at y=L
  const g = lathe([
    [0, 0], [R * 0.32, 0.02], [R * 0.36, 0.1], [R * 0.2, 0.16], [R * 0.85, 0.2], [R * 0.98, 0.26],
    [R * 0.98, 0.34], [R * 0.9, 0.36], [R * 0.86, L * 0.55], [R * 0.94, L * 0.56], [R * 0.94, L * 0.6],
    [R * 0.78, L * 0.62], [R * 0.72, L * 0.88], [R * 0.9, L * 0.92], [R * 0.96, L * 0.98], [R * 0.6, L],
    [R * 0.42, L], [R * 0.42, L - 0.25],
  ], 14, 1, 1);
  g.translate(0, -L / 2, 0);
  g.rotateZ(b.tag === "shipGunE" ? -Math.PI / 2 : Math.PI / 2); // +y → muzzle direction
  g.translate(b.p[0], b.p[1], b.p[2]);
  return paint(g, "#2a2a2e");
}

// ---------------------------------------------------------------------------
// SAILCLOTH texture — near-white, seamed, with reef bands; tinted by vertex
// colour (sail vs sailDark) like every other family.
// ---------------------------------------------------------------------------
let clothTex: THREE.CanvasTexture | null = null;
function sailcloth(): THREE.CanvasTexture {
  if (clothTex) return clothTex;
  const W = 512, H = 512;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const g = c.getContext("2d")!;
  g.fillStyle = "#f2ede3";
  g.fillRect(0, 0, W, H);
  // deterministic speckle — weave + weathering
  let s = 1234567;
  const rnd = (): number => ((s = (s * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
  for (let i = 0; i < 9000; i++) {
    const v = 200 + Math.floor(rnd() * 50);
    g.fillStyle = `rgba(${v},${v - 6},${v - 18},0.18)`;
    g.fillRect(rnd() * W, rnd() * H, 1 + rnd() * 2, 1 + rnd() * 3);
  }
  // vertical CLOTHS: real sails are sewn from ~0.6 m bolts; 16 per texture
  for (let i = 1; i < 16; i++) {
    const x = (i * W) / 16;
    g.fillStyle = "rgba(120,100,70,0.35)"; g.fillRect(x - 1, 0, 2, H);
    g.fillStyle = "rgba(255,255,250,0.45)"; g.fillRect(x + 1, 0, 2, H);
  }
  // REEF BANDS with reef points (the little tie lines) across the upper half
  for (const yf of [0.16, 0.3, 0.44]) {
    const y = yf * H;
    g.fillStyle = "rgba(140,120,90,0.45)"; g.fillRect(0, y - 3, W, 6);
    g.fillStyle = "rgba(80,64,44,0.8)";
    for (let x = 12; x < W; x += 24) g.fillRect(x, y - 2, 2, 14);
  }
  // BOLT ROPE + tabling round the edges, grime toward the foot
  g.strokeStyle = "rgba(90,74,52,0.9)"; g.lineWidth = 8; g.strokeRect(4, 4, W - 8, H - 8);
  const grime = g.createLinearGradient(0, H * 0.55, 0, H);
  grime.addColorStop(0, "rgba(110,95,70,0)"); grime.addColorStop(1, "rgba(110,95,70,0.28)");
  g.fillStyle = grime; g.fillRect(0, 0, W, H);
  clothTex = new THREE.CanvasTexture(c);
  clothTex.colorSpace = THREE.SRGBColorSpace;
  clothTex.anisotropy = 4;
  return clothTex;
}

/** Build the whole fleet's dressing. `shadows` = the renderer's shadow tier. */
export function buildShipDressing(boxes: readonly MapBox[], shadows: boolean): THREE.Group {
  const group = new THREE.Group();
  group.name = "shipDressing";
  const spars: THREE.BufferGeometry[] = [];
  const sails: THREE.BufferGeometry[] = [];
  const iron: THREE.BufferGeometry[] = [];
  const ropes: THREE.BufferGeometry[] = [];
  const paintG: THREE.BufferGeometry[] = [];
  const glass: THREE.BufferGeometry[] = [];

  for (const b of boxes) {
    if (b.tag === "shipSpar") spars.push(...sparGeo(b));
    else if (b.tag === "shipSail") sails.push(sailGeo(b));
    else if (b.tag === "shipGunE" || b.tag === "shipGunW") iron.push(gunGeo(b));
  }
  // BOWSPRITS — one per ship, a spar out over the figurehead
  for (const ship of shipsOf(shipMasts(boxes))) {
    const bs = bowsprit(ship[ship.length - 1]);
    _va.set(...bs.a); _vb.set(...bs.b);
    const L = _va.distanceTo(_vb);
    const g = lathe([[0, 0], [0.34, 0], [0.3, L * 0.5], [0.18, L], [0, L]], 10, 1.2, 2.4);
    _q.setFromUnitVectors(_up, _vb.clone().sub(_va).normalize());
    g.applyQuaternion(_q);
    g.translate(...bs.a);
    spars.push(paint(g, "#4a3320"));
  }
  const rig = rigLayout(boxes);
  for (const r of rig.ropes) ropes.push(paint(rope(r, r.r < 0.03 ? 3 : 5), "#2b2219"));
  // CHANNELS: the ledge the shrouds land on, with a deadeye at each foot
  for (const c of rig.channels) {
    const g = new THREE.BoxGeometry(Math.abs(c.x1 - c.x0), 0.14, c.z1 - c.z0).toNonIndexed();
    g.translate((c.x0 + c.x1) / 2, c.y - 0.07, (c.z0 + c.z1) / 2);
    spars.push(paint(g, "#3f2a19"));
  }
  for (const r of rig.ropes) {
    if (r.r < 0.04 || Math.abs(r.b[1] - CHANNEL_Y) > 1e-6) continue;
    const de = new THREE.CylinderGeometry(0.11, 0.11, 0.08, 8).toNonIndexed();
    de.rotateZ(Math.PI / 2);
    de.translate(r.b[0], r.b[1] + 0.1, r.b[2]);
    iron.push(paint(de, "#1e1712"));
  }

  // GUNPORTS: dark opening, ochre-red lining, timber frame, iron muzzle
  for (const p of shipGunports(boxes)) {
    const W = 0.86, H = 0.74;
    const hole = new THREE.PlaneGeometry(W, H).toNonIndexed();
    hole.rotateY(p.nx > 0 ? Math.PI / 2 : -Math.PI / 2);
    hole.translate(p.x + p.nx * 0.015, p.y, p.z);
    paintG.push(paint(hole, "#0d0b09"));
    const F = 0.12, P = 0.09; // frame width, proud
    for (const [fw, fh, dy, dz] of [[W + 2 * F, F, H / 2 + F / 2, 0], [W + 2 * F, F, -H / 2 - F / 2, 0], [F, H, 0, W / 2 + F / 2], [F, H, 0, -W / 2 - F / 2]] as const) {
      const fr = new THREE.BoxGeometry(P, fh, fw === F ? F : fw).toNonIndexed();
      fr.deleteAttribute("uv");
      fr.translate(p.x + p.nx * P / 2, p.y + dy, p.z + dz);
      paintG.push(paint(fr, "#7a2f1f"));
    }
    // the gun's muzzle just inside the port, 0.2 m proud of the hull
    const mz = lathe([[0, 0], [0.17, 0], [0.17, 0.32], [0.21, 0.36], [0.21, 0.44], [0.12, 0.44], [0.12, 0.3], [0, 0.3]], 12, 1, 1);
    mz.rotateZ(p.nx > 0 ? -Math.PI / 2 : Math.PI / 2);
    mz.translate(p.x - p.nx * 0.24, p.y - 0.05, p.z);
    iron.push(paint(mz, "#2a2a2e"));
  }

  // STERN GALLERY: a row of leaded lights in gilt frames on each transom's
  // outward (aft) face, under the quarterdeck.
  for (const b of boxes) {
    if (b.tag !== "shipStern") continue;
    const nz = -1; // transoms close the stern, which is −z on every hull
    const face = b.p[2] + nz * b.s[2] / 2;
    const n = 5, WW = 1.25, WH = 1.5, gap = (b.s[0] - 2 - n * WW) / (n - 1);
    const y = b.p[1] + b.s[1] / 2 - 2.0;
    for (let i = 0; i < n; i++) {
      const x = b.p[0] - (n * WW + (n - 1) * gap) / 2 + WW / 2 + i * (WW + gap);
      const pane = new THREE.PlaneGeometry(WW, WH).toNonIndexed();
      pane.rotateY(Math.PI);
      pane.translate(x, y, face + nz * 0.03);
      glass.push(paint(pane, "#ffffff"));
      const F = 0.13, P = 0.12;
      for (const [fw, fh, dx, dy] of [[WW + 2 * F, F, 0, WH / 2 + F / 2], [WW + 2 * F, F * 1.6, 0, -WH / 2 - F * 0.8], [F, WH, WW / 2 + F / 2, 0], [F, WH, -WW / 2 - F / 2, 0], [0.05, WH, 0, 0], [WW, 0.05, 0, 0]] as const) {
        const fr = new THREE.BoxGeometry(fw, fh, P).toNonIndexed();
        fr.deleteAttribute("uv");
        fr.translate(x + dx, y + dy, face + nz * P / 2);
        paintG.push(paint(fr, "#b8923a"));
      }
    }
  }

  const add = (geos: THREE.BufferGeometry[], mat: THREE.Material, cast: boolean): void => {
    if (!geos.length) return;
    // uv is not on every piece (ropes / frames drop it) — merge needs a match
    const anyUv = geos.some((g) => g.getAttribute("uv"));
    if (anyUv) for (const g of geos) if (!g.getAttribute("uv")) g.setAttribute("uv", new THREE.BufferAttribute(new Float32Array(g.getAttribute("position").count * 2), 2));
    for (const g of geos) if (g.getAttribute("normal") === undefined) g.computeVertexNormals();
    const merged = mergeGeometries(geos);
    for (const g of geos) g.dispose();
    if (!merged) { console.error("[ship] merge failed"); return; }
    const mesh = new THREE.Mesh(merged, mat);
    mesh.castShadow = cast && shadows;
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false; // static: identity transform, baked once
    group.add(mesh);
  };
  add(spars, new THREE.MeshStandardMaterial(surfaceMaps("wood", { color: "#ffffff", vertexColors: true, roughness: 0.9, metalness: 0 }, 0.7)), true);
  add(sails, new THREE.MeshStandardMaterial({
    map: sailcloth(), color: "#ffffff", vertexColors: true, roughness: 0.95, metalness: 0,
    side: THREE.DoubleSide,
    // canvas glows a little with the sun behind it — a cheap translucency cue
    emissive: "#3a352c", emissiveIntensity: 0.35,
  }), true);
  add(iron, new THREE.MeshStandardMaterial({ color: "#ffffff", vertexColors: true, roughness: 0.42, metalness: 0.65 }), true);
  add(ropes, new THREE.MeshStandardMaterial({ color: "#ffffff", vertexColors: true, roughness: 0.95, metalness: 0 }), true);
  add(paintG, new THREE.MeshStandardMaterial({ color: "#ffffff", vertexColors: true, roughness: 0.6, metalness: 0.2 }), false);
  add(glass, new THREE.MeshStandardMaterial({ color: "#1a2228", roughness: 0.1, metalness: 0, envMapIntensity: 1.2, emissive: "#6b4a1c", emissiveIntensity: 0.25 }), false);
  return group;
}
